import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { DEFAULT_CANCEL_REASON } from "@/api/ApiClient";
import { isSuccess } from "@/api/ApiResult";
import { useCrud } from "@/api/useCrud";
import { MissingCommitteeSessionDetailsModal } from "@/components/committee_session/MissingCommitteeSessionDetailsModal";
import { Footer } from "@/components/footer/Footer";
import { IconTrash } from "@/components/generated/icons";
import { Messages } from "@/components/messages/Messages";
import { PageTitle } from "@/components/page_title/PageTitle";
import { Alert } from "@/components/ui/Alert/Alert";
import { Button } from "@/components/ui/Button/Button";
import { Modal } from "@/components/ui/Modal/Modal";
import { Table } from "@/components/ui/Table/Table";
import { useElection } from "@/hooks/election/useElection";
import { useUserRole } from "@/hooks/user/useUserRole";
import { t, tx } from "@/i18n/translate";
import type {
  COMMITTEE_SESSION_CREATE_REQUEST_PATH,
  COMMITTEE_SESSION_DELETE_REQUEST_PATH,
  CommitteeSession,
  DISMISS_PUBLIC_KEY_UPLOAD_REMINDER_REQUEST_PATH,
  Election,
} from "@/types/generated/openapi";
import { cn } from "@/utils/classnames";
import { committeeSessionDetailsPresent, committeeSessionLabel } from "@/utils/committeeSession";
import { directDownload } from "@/utils/download";
import { hasBooleanProperty } from "@/utils/typeChecks";
import { CommitteeSessionCard } from "./CommitteeSessionCard";
import { ElectionInformationTable } from "./ElectionInformationTable";
import cls from "./ElectionManagement.module.css";

interface DownloadSectionProps {
  election: Election;
  committeeSession: CommitteeSession;
}

function CSOFirstSessionDownloadSection({ election }: DownloadSectionProps) {
  return (
    <div id="CSO-first-session-download-section" className="mt-xl">
      <h3 className={cls.tableTitle}>{t("election_management.empty_documents_first_session")}</h3>
      <p className={cls.downloadDescription}>{t("election_management.empty_document_description.plural")}</p>
      <Table className={cn(cls.electionInformationTable)} variant="information">
        <Table.Header>
          <Table.HeaderCell scope="col">{t("election_management.document_model")}</Table.HeaderCell>
          <Table.HeaderCell scope="col">{t("election_management.document_purpose")}</Table.HeaderCell>
        </Table.Header>
        <Table.Body>
          <Table.ClickRow
            downloadIcon
            onClick={() => {
              directDownload(`/api/elections/${election.id}/download_n_10_2`);
            }}
          >
            <Table.Cell>N 10-2</Table.Cell>
            <Table.Cell>{t("election_management.document_n_10_1_and_n_10_2")}</Table.Cell>
          </Table.ClickRow>
          <Table.ClickRow
            downloadIcon
            onClick={() => {
              directDownload(`/api/elections/${election.id}/download_na_31_2_bijlage1`);
            }}
          >
            <Table.Cell>Na 31-2 Bijlage 1</Table.Cell>
            <Table.Cell>{t("election_management.document_na_31_2_bijlage_1")}</Table.Cell>
          </Table.ClickRow>
        </Table.Body>
      </Table>
    </div>
  );
}

function DSOFirstSessionDownloadSection({ election, committeeSession }: DownloadSectionProps) {
  const [showMissingCommitteeSessionDetailsModal, setShowMissingCommitteeSessionDetailsModal] = useState(false);

  return (
    <div id="DSO-first-session-download-section" className="mt-xl">
      <h3 className={cls.tableTitle}>{t("election_management.empty_documents_first_session")}</h3>
      <p className={cls.downloadDescription}>{t("election_management.empty_document_description.plural")}</p>
      <Table className={cn(cls.electionInformationTable)} variant="information">
        <Table.Header>
          <Table.HeaderCell scope="col">{t("election_management.document_model")}</Table.HeaderCell>
          <Table.HeaderCell scope="col">{t("election_management.document_purpose")}</Table.HeaderCell>
        </Table.Header>
        <Table.Body>
          <Table.ClickRow
            downloadIcon
            onClick={() => {
              directDownload(`/api/elections/${election.id}/download_n_10_1`);
            }}
          >
            <Table.Cell>N 10-1</Table.Cell>
            <Table.Cell>{t("election_management.document_n_10_1_and_n_10_2")}</Table.Cell>
          </Table.ClickRow>
          <Table.ClickRow
            downloadIcon
            onClick={() => {
              directDownload(`/api/elections/${election.id}/download_n_10_1_inlegvel`);
            }}
          >
            <Table.Cell>N 10-1</Table.Cell>
            <Table.Cell>{t("election_management.document_n_10_1_inlegvel")}</Table.Cell>
          </Table.ClickRow>
          <Table.ClickRow
            downloadIcon
            onClick={() => {
              if (committeeSessionDetailsPresent(committeeSession)) {
                directDownload(`/api/elections/${election.id}/download_na_14_1_versie1`);
              } else {
                setShowMissingCommitteeSessionDetailsModal(true);
              }
            }}
          >
            <Table.Cell>Na 14-1, versie 1</Table.Cell>
            <Table.Cell>{t("election_management.document_na_14_1_versie_1")}</Table.Cell>
          </Table.ClickRow>
        </Table.Body>
      </Table>
      {showMissingCommitteeSessionDetailsModal && (
        <MissingCommitteeSessionDetailsModal
          to="details"
          onClose={() => {
            setShowMissingCommitteeSessionDetailsModal(false);
          }}
        />
      )}
    </div>
  );
}

function CSONextSessionDownloadSection({ election }: DownloadSectionProps) {
  return (
    <div id="CSO-next-session-download-section" className="mt-xl">
      <h3 className={cls.tableTitle}>{t("election_management.empty_document_next_session.singular")}</h3>
      <p className={cls.downloadDescription}>{t("election_management.empty_document_description.singular")}</p>
      <Table className={cn(cls.electionInformationTable)} variant="information">
        <Table.Header>
          <Table.HeaderCell scope="col">{t("election_management.document_model")}</Table.HeaderCell>
          <Table.HeaderCell scope="col">{t("election_management.document_purpose")}</Table.HeaderCell>
        </Table.Header>
        <Table.Body>
          <Table.ClickRow
            downloadIcon
            onClick={() => {
              directDownload(`/api/elections/${election.id}/download_na_31_2_inlegvel`);
            }}
          >
            <Table.Cell>Na 31-2 inlegvel</Table.Cell>
            <Table.Cell>{t("election_management.document_na_31_2_inlegvel")}</Table.Cell>
          </Table.ClickRow>
        </Table.Body>
      </Table>
    </div>
  );
}

function DSONextSessionDownloadSection({ election }: DownloadSectionProps) {
  return (
    <div id="DSO-next-session-download-section" className="mt-xl">
      <h3 className={cls.tableTitle}>{t("election_management.empty_document_next_session.plural")}</h3>
      <p className={cls.downloadDescription}>{t("election_management.empty_document_description.plural")}</p>
      <Table className={cn(cls.electionInformationTable)} variant="information">
        <Table.Header>
          <Table.HeaderCell scope="col">{t("election_management.document_model")}</Table.HeaderCell>
          <Table.HeaderCell scope="col">{t("election_management.document_purpose")}</Table.HeaderCell>
        </Table.Header>
        <Table.Body>
          <Table.ClickRow
            downloadIcon
            onClick={() => {
              directDownload(`/api/elections/${election.id}/download_n_10_1_inlegvel`);
            }}
          >
            <Table.Cell>N 10-1</Table.Cell>
            <Table.Cell>{t("election_management.document_n_10_1_inlegvel")}</Table.Cell>
          </Table.ClickRow>
          <Table.ClickRow
            downloadIcon
            onClick={() => {
              directDownload(`/api/elections/${election.id}/download_na_31_1_inlegvel`);
            }}
          >
            <Table.Cell>Na 31-1 inlegvel</Table.Cell>
            <Table.Cell>{t("election_management.document_na_31_1_inlegvel")}</Table.Cell>
          </Table.ClickRow>
        </Table.Body>
      </Table>
    </div>
  );
}

function Alerts() {
  const { election, pollingStations, showKeypairReminder, refetch } = useElection();
  const { role } = useUserRole();

  const dismissReminderPath: DISMISS_PUBLIC_KEY_UPLOAD_REMINDER_REQUEST_PATH = `/api/elections/${election.id}/dismiss_public_key_upload_reminder`;
  const { update: dismissReminder, isLoading: isUpdatingReminder } = useCrud({
    updatePath: dismissReminderPath,
    throwAllErrors: true,
  });

  if (election.committee_category !== "GSB") {
    return null;
  }

  return (
    <>
      {role === "administrator" && showKeypairReminder && (
        <Alert type="notify">
          <strong className="heading-md">{t("election_management.alert_register.title")}</strong>
          <p>
            {tx("election_management.alert_register.explanation", undefined, {
              committee: t(`committee_category.${election.committee_category}.short`).toLowerCase(),
            })}
          </p>
          <nav className={cls.alertActions}>
            <Button.Link to="certificate" size="md">
              {t("election_management.alert_register.register")}
            </Button.Link>
            <Button
              variant="secondary"
              size="md"
              disabled={showKeypairReminder === "NonDismissable" || isUpdatingReminder}
              onClick={() => {
                void dismissReminder({}).then((result) => {
                  if (isSuccess(result)) {
                    void refetch();
                  }
                });
              }}
            >
              {t("election_management.alert_register.register_already_done")}
            </Button>
          </nav>
        </Alert>
      )}
      {pollingStations.length === 0 && (
        <Alert type="warning">
          <strong className="heading-md" id="noPollingStationsWarningAlertTitle">
            {t("election_management.no_polling_stations")}
          </strong>
          <p id="noPollingStationsWarningAlertDescription">{t("election_management.add_polling_stations_first")}</p>
          <p>
            <Link to="polling-stations">{t("polling_station.manage")}</Link> →
          </p>
        </Alert>
      )}
    </>
  );
}

interface CommitteeSessionModalsProps {
  showAddModal: boolean;
  onCloseAddModal: () => void;
}

function CommitteeSessionModals({ showAddModal, onCloseAddModal }: CommitteeSessionModalsProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentCommitteeSession, election, investigations, refetch } = useElection();
  const showDeleteModal = hasBooleanProperty(location.state, "showDeleteModal") && location.state.showDeleteModal;

  const createPath: COMMITTEE_SESSION_CREATE_REQUEST_PATH = `/api/elections/${election.id}/committee_sessions`;
  const removePath: COMMITTEE_SESSION_DELETE_REQUEST_PATH = `/api/elections/${currentCommitteeSession.election_id}/committee_sessions/${currentCommitteeSession.id}`;
  const { create, remove } = useCrud({ createPath, removePath, throwAllErrors: true });

  function closeDeleteModal() {
    void navigate(".", { replace: true });
  }

  if (showAddModal) {
    return (
      <Modal title={t("election_management.investigation_ordered_by_csb")} onClose={onCloseAddModal}>
        <p>{t("election_management.only_add_if_ordered")}</p>
        <p>{t("election_management.if_gsb_correction")}</p>
        <nav>
          <Button
            variant="primary"
            size="xl"
            onClick={() => {
              void create({}).then(() => void refetch());
              onCloseAddModal();
            }}
          >
            {t("election_management.yes_add_session")}
          </Button>
          <Button variant="secondary" onClick={onCloseAddModal}>
            {t("cancel")}
          </Button>
        </nav>
      </Modal>
    );
  }

  if (!showDeleteModal) {
    return null;
  }

  if (investigations.length > 0) {
    return (
      <Modal title={t("election_management.delete_investigations_first")} onClose={closeDeleteModal}>
        <p>
          {t("election_management.delete_investigations_first_are_you_sure", {
            sessionLabel: committeeSessionLabel(
              election.committee_category,
              currentCommitteeSession.number,
              true,
              true,
            ),
          })}
        </p>
        <nav>
          <Button variant="primary" size="xl" onClick={() => void navigate("investigations")}>
            {t("election_management.view_investigations")}
          </Button>
          <Button variant="secondary" onClick={closeDeleteModal}>
            {t("cancel")}
          </Button>
        </nav>
      </Modal>
    );
  }

  return (
    <Modal title={`${t("election_management.delete_session")}?`} onClose={closeDeleteModal}>
      <p>
        {t("election_management.delete_session_are_you_sure", {
          sessionLabel: committeeSessionLabel(election.committee_category, currentCommitteeSession.number, true, true),
        })}
      </p>
      <nav>
        <Button
          leftIcon={<IconTrash />}
          variant="primary-destructive"
          size="xl"
          onClick={() => {
            void remove().then(() => void refetch());
            closeDeleteModal();
          }}
        >
          {t("election_management.yes_delete_session")}
        </Button>
        <Button variant="secondary" onClick={closeDeleteModal}>
          {t("cancel")}
        </Button>
      </nav>
    </Modal>
  );
}

export function ElectionHomePage() {
  const { currentCommitteeSession, committeeSessions, election, pollingStations, showKeypairReminder, refetch } =
    useElection();
  const { isCoordinator, role } = useUserRole();
  const [showAddCommitteeSessionModal, setShowAddCommitteeSessionModal] = useState(false);

  // re-fetch election when component mounts
  useEffect(() => {
    const abortController = new AbortController();
    void refetch(abortController);
    return () => {
      abortController.abort(DEFAULT_CANCEL_REASON);
    };
  }, [refetch]);

  return (
    <>
      <PageTitle title={`${t("election.title.details")} - Abacus`} />
      <header>
        <section>
          <h1>{election.name}</h1>
        </section>
      </header>
      <Messages />
      <Alerts />
      <CommitteeSessionModals
        showAddModal={showAddCommitteeSessionModal}
        onCloseAddModal={() => {
          setShowAddCommitteeSessionModal(false);
        }}
      />
      <main className={cls.electionHome}>
        <article>
          <div className="mb-xl">
            <div>
              <h2>
                {t(`committee_category.${election.committee_category}.short`)} {election.location}
              </h2>
            </div>
          </div>
          <div id="committee-sessions" className={cn(cls.sessions, "mb-xl")}>
            <div id="committee-session-cards" className={cls.cards}>
              {committeeSessions.map((committeeSession, index) => (
                <CommitteeSessionCard
                  key={committeeSession.id}
                  committeeSession={committeeSession}
                  committeeCategory={election.committee_category}
                  isCurrentSession={index === 0}
                />
              ))}
            </div>
            {isCoordinator &&
              election.committee_category === "GSB" &&
              currentCommitteeSession.status === "completed" && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShowAddCommitteeSessionModal(true);
                  }}
                >
                  {t("election_management.prepare_new_session")}
                </Button>
              )}
          </div>
          <div className={cn(cls.line, "mb-xl")}></div>
          <div>
            <h3 className={cls.tableTitle}>{t("election_management.about_this_election")}</h3>
            <ElectionInformationTable
              election={election}
              committeeSession={currentCommitteeSession}
              numberOfPollingStations={pollingStations.length}
              role={role}
              publicKeyRegistered={!showKeypairReminder}
            />
          </div>
          {election.committee_category === "GSB" &&
            (currentCommitteeSession.number === 1 ? (
              election.counting_method === "CSO" ? (
                <CSOFirstSessionDownloadSection election={election} committeeSession={currentCommitteeSession} />
              ) : (
                <DSOFirstSessionDownloadSection election={election} committeeSession={currentCommitteeSession} />
              )
            ) : election.counting_method === "CSO" ? (
              <CSONextSessionDownloadSection election={election} committeeSession={currentCommitteeSession} />
            ) : (
              <DSONextSessionDownloadSection election={election} committeeSession={currentCommitteeSession} />
            ))}
        </article>
      </main>
      <Footer />
    </>
  );
}
